import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import InlineBoldText from '@/components/atoms/InlineBoldText/InlineBoldText';
import StatusBadge from '@/components/atoms/StatusBadge/StatusBadge';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import {
    type CreateSmoltReportBody,
    isCreatingPlaceholder,
    type SmoltReportListItemRow,
} from '@/domain/smoltReport';
import TwoStepDeletionButton from '@/routes/Client/Cage/pages/Reports/shared/TwoStepDeletionButton';
import { formatDayDisplay, formattedTs } from '@/utils/date';

import { CreateSmoltReportModal } from './CreateSmoltReportModal';

import styles from './SmoltReportList.module.scss';

const ROW_HEIGHT = 52;
/** A row with the "whole tank" note under its status badge needs room for both lines. */
const ROW_HEIGHT_WITH_NOTE = 68;
const SKELETON_HEIGHT = ROW_HEIGHT * 2;

interface SmoltReportListProps {
    isLoading?: boolean;
    reports?: SmoltReportListItemRow[];
    onCreateReport: (body: CreateSmoltReportBody) => Promise<void>;
    /**
     * A fish group is selected, so reports computed for the whole tank are marked: cage and
     * group reports share the list while the fish-group cutover lets them coexist.
     */
    isFishGroupSelected?: boolean;
    /**
     * False on a pen running a Bioscope 1.0: it measures no Laksvel, so a smolt report cannot be
     * created there. Reports stored before stay listed.
     */
    canCreate?: boolean;
}

export default function SmoltReportList(props: SmoltReportListProps) {
    const {
        reports = [],
        isLoading = false,
        onCreateReport,
        isFishGroupSelected = false,
        canCreate = true,
    } = props;

    const { t } = useTranslation(['smoltReport', 'common']);

    const showsCageLevelNote = (report: SmoltReportListItemRow) =>
        isFishGroupSelected && report.fishGroupId == null;

    const columns: DataTableColumnDef<SmoltReportListItemRow>[] = [
        {
            id: 'status',
            header: t('smoltReport:reportList.status'),
            cell: ({ row: { original: report } }) => (
                <div className={styles.statusContainer}>
                    <StatusBadge className={styles.statusBadge} status={report.status} />
                    {showsCageLevelNote(report) && (
                        <span
                            className={styles.cageLevelNote}
                            title={t('smoltReport:reportList.cageLevelNotice')}>
                            {t('smoltReport:reportList.cageLevel')}
                        </span>
                    )}
                </div>
            ),
        },
        {
            id: 'stockingDate',
            header: t('smoltReport:reportList.stockingDate'),
            cell: ({ row }) => formatDayDisplay(row.original.stockingDate) || '-',
        },
        {
            id: 'lastFeedingDate',
            header: t('smoltReport:reportList.lastFeedingDate'),
            cell: ({ row }) => formatDayDisplay(row.original.lastFeedingDate) || '-',
        },
        {
            id: 'deliveryDate',
            header: t('smoltReport:reportList.deliveryDate'),
            cell: ({ row }) => formatDayDisplay(row.original.deliveryDate) || '-',
        },
        {
            id: 'createdAt',
            header: t('smoltReport:reportList.createdAt'),
            cell: ({ row }) => (row.original.createdAt ? formattedTs(row.original.createdAt) : '-'),
        },
        {
            id: 'actions',
            header: t('smoltReport:reportList.actions'),
            cell: ({ row: { original: report } }) => {
                const isCreating = isCreatingPlaceholder(report);
                return (
                    <div className={styles.actionsContainer}>
                        <Button
                            onClick={() => report.onClick?.()}
                            variant="table"
                            trackingId="smolt_report_show"
                            disabled={
                                isCreating ||
                                (report.status !== 'finished' && report.status !== 'warning')
                            }
                            size={'medium'}>
                            {t('common:actions.show')}
                        </Button>
                        {!isCreating && (
                            <TwoStepDeletionButton
                                onConfirm={() => report.onDelete?.()}
                                isDeleting={report.isDeleting}
                            />
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <ContentBlock>
            {canCreate ? (
                <CreateSmoltReportModal onSubmitted={onCreateReport} />
            ) : (
                <p className={styles.unavailableNotice}>
                    {t('smoltReport:reportList.bioscopeV1Notice')}
                </p>
            )}
            <DataTable
                data={reports}
                columns={columns}
                getRowId={(report) => String(report.id)}
                layout="grid"
                enableSorting={false}
                rowHeight={ROW_HEIGHT}
                isLoading={isLoading}
                skeletonHeight={SKELETON_HEIGHT}
                getRowProps={(row) =>
                    showsCageLevelNote(row.original) ? { rowHeight: ROW_HEIGHT_WITH_NOTE } : {}
                }
                emptyState={
                    <EmptyState
                        appearance="quiet"
                        size="spacious"
                        title={
                            <InlineBoldText
                                text={t(
                                    canCreate
                                        ? 'smoltReport:reportList.emptyList'
                                        : 'smoltReport:reportList.emptyListNoCreate'
                                )}
                            />
                        }
                    />
                }
            />
        </ContentBlock>
    );
}
